import { supabase, hasValidCredentials } from './supabase'
import type { Database } from './supabase'

// Type aliases
type JournalistDb = Database['public']['Tables']['journalists']['Row']

// Frontend-compatible types
export interface Journalist {
  id: string
  name: string
  outlet: string
  email?: string
  phone?: string
  specialty?: string
  bio?: string
  twitter?: string
  website?: string
  verified: boolean
  credentialsPickedUp: boolean
  pressTier: 'A' | 'B' | 'C'
  notes?: string
  source: string
}

// Conversion functions
const convertJournalist = (dbJournalist: JournalistDb): Journalist => ({
  id: dbJournalist.id,
  name: dbJournalist.name,
  outlet: dbJournalist.outlet,
  email: dbJournalist.email || undefined,
  phone: dbJournalist.phone || undefined,
  specialty: dbJournalist.specialty || undefined,
  bio: dbJournalist.bio || undefined,
  twitter: dbJournalist.twitter || undefined,
  website: dbJournalist.website || undefined,
  verified: dbJournalist.verified,
  credentialsPickedUp: dbJournalist.credentials_picked_up,
  pressTier: ((dbJournalist as any).press_tier as 'A' | 'B' | 'C') || 'C',
  notes: dbJournalist.notes || undefined,
  source: dbJournalist.source || 'manual'
})

// Journalist service
export const journalistService = {
  async getAll(eventYear?: number): Promise<Journalist[]> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - returning empty journalists array')
      return []
    }

    try {
      let query = supabase
        .from('journalists')
        .select('*')
      if (eventYear) query = query.eq('event_year', eventYear)
      const { data, error } = await query.order('created_at', { ascending: false })
      
      if (error) throw error
      return (data || []).map(convertJournalist)
    } catch (error) {
      console.error('Error fetching journalists:', error)
      return []
    }
  },

  async create(journalist: Omit<Journalist, 'id'>, eventYear?: number): Promise<Journalist | null> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot save journalist')
      return null
    }

    try {
      // Check if journalist already exists for this year (only if they have an email)
      if (journalist.email) {
        let checkQuery = supabase
          .from('journalists')
          .select('*')
          .eq('email', journalist.email)
        if (eventYear) checkQuery = checkQuery.eq('event_year', eventYear)
        const { data: existing } = await checkQuery.single()

        if (existing) {
          console.log('Journalist already exists, updating instead of creating:', journalist.email)
          // Update existing journalist
          const { data, error } = await supabase
            .from('journalists')
            .update({
              name: journalist.name,
              outlet: journalist.outlet,
              phone: journalist.phone || null,
              specialty: journalist.specialty || null,
              bio: journalist.bio || null,
              twitter: journalist.twitter || null,
              website: journalist.website || null,
              verified: journalist.verified,
              credentials_picked_up: journalist.credentialsPickedUp,
              notes: journalist.notes || null,
              source: journalist.source || 'manual'
            })
            .eq('email', journalist.email)
            .select()
            .single()

          if (error) throw error
          return data ? convertJournalist(data) : null
        }
      }

      // Create new journalist
      const insertData: any = {
          name: journalist.name,
          outlet: journalist.outlet,
          email: journalist.email || null,
          phone: journalist.phone || null,
          specialty: journalist.specialty || null,
          bio: journalist.bio || null,
          twitter: journalist.twitter || null,
          website: journalist.website || null,
          verified: journalist.verified,
          credentials_picked_up: journalist.credentialsPickedUp,
          notes: journalist.notes || null,
          source: journalist.source || 'manual'
      }
      if (eventYear) insertData.event_year = eventYear
      const { data, error } = await supabase
        .from('journalists')
        .insert(insertData)
        .select()
        .single()

      if (error) throw error
      return data ? convertJournalist(data) : null
    } catch (error) {
      console.error('Error creating journalist:', error)
      throw error
    }
  },

  async bulkCreate(journalists: Omit<Journalist, 'id'>[], eventYear?: number): Promise<Journalist[]> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot save journalists')
      return []
    }

    console.log('📝 Service: Starting bulkCreate for', journalists.length, 'journalists');

    try {
      const results: Journalist[] = []
      const errors: Array<{ name: string; error: string }> = []

      // Process each journalist individually to handle duplicates
      for (const journalist of journalists) {
        try {
          let existing = null;

          // Only check for duplicates if journalist has an email
          if (journalist.email) {
            console.log(`🔍 Service: Checking if journalist exists: ${journalist.email}`);

            let checkQuery = supabase
              .from('journalists')
              .select('*')
              .eq('email', journalist.email)
            if (eventYear) checkQuery = checkQuery.eq('event_year', eventYear)
            const { data: existingData, error: checkError } = await checkQuery.single()

            if (checkError && checkError.code !== 'PGRST116') {
              console.error(`❌ Service: Error checking journalist ${journalist.email}:`, checkError);
            }
            existing = existingData;
          }

          if (existing) {
            console.log(`Journalist with email ${journalist.email} already exists, updating...`)
            const { data, error } = await supabase
              .from('journalists')
              .update({
                name: journalist.name,
                outlet: journalist.outlet,
                phone: journalist.phone || null,
                specialty: journalist.specialty || null,
                bio: journalist.bio || null,
                twitter: journalist.twitter || null,
                website: journalist.website || null,
                verified: journalist.verified,
                credentials_picked_up: journalist.credentialsPickedUp,
                notes: journalist.notes || null,
                source: journalist.source || 'bulk_upload'
              })
              .eq('email', journalist.email!)
              .select()
              .single()

            if (error) {
              errors.push({ name: journalist.name, error: error.message })
            } else if (data) {
              results.push(convertJournalist(data))
            }
          } else {
            // Create new journalist
            const insertData: any = {
                name: journalist.name,
                outlet: journalist.outlet,
                email: journalist.email || null,
                phone: journalist.phone || null,
                specialty: journalist.specialty || null,
                bio: journalist.bio || null,
                twitter: journalist.twitter || null,
                website: journalist.website || null,
                verified: journalist.verified,
                credentials_picked_up: journalist.credentialsPickedUp,
                notes: journalist.notes || null,
                source: journalist.source || 'bulk_upload'
            }
            if (eventYear) insertData.event_year = eventYear
            const { data, error } = await supabase
              .from('journalists')
              .insert(insertData)
              .select()
              .single()
            
            if (error) {
              errors.push({ name: journalist.name, error: error.message })
            } else if (data) {
              results.push(convertJournalist(data))
            }
          }
        } catch (error) {
          errors.push({
            name: journalist.name,
            error: error instanceof Error ? error.message : 'Unknown error'
          })
        }
      }

      // Log any errors
      if (errors.length > 0) {
        console.warn('Some journalists failed to import:', errors)
      }

      return results
    } catch (error) {
      console.error('Error bulk creating journalists:', error)
      throw error
    }
  },

  async bulkUpsert(journalists: Omit<Journalist, 'id'>[], eventYear?: number): Promise<{ added: Journalist[], updated: Journalist[], skipped: string[] }> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot upsert journalists')
      return { added: [], updated: [], skipped: [] }
    }

    try {
      const added: Journalist[] = []
      const updated: Journalist[] = []
      const skipped: string[] = []

      // Get all existing journalists by email for comparison (only those with emails)
      const emails = journalists.map(j => j.email).filter((e): e is string => !!e)
      const existingByEmail = new Map<string, JournalistDb>()

      if (emails.length > 0) {
        let fetchQuery = supabase
          .from('journalists')
          .select('*')
          .in('email', emails)
        if (eventYear) fetchQuery = fetchQuery.eq('event_year', eventYear)
        const { data: existingJournalists, error: fetchError } = await fetchQuery

        if (fetchError) throw fetchError
        existingJournalists?.forEach(j => {
          if (j.email) existingByEmail.set(j.email, j)
        })
      }

      // Separate journalists into those to insert and those to update
      const toInsert: any[] = []
      const toUpdate: { email: string, data: any }[] = []

      for (const journalist of journalists) {
        const existing = journalist.email ? existingByEmail.get(journalist.email) : undefined

        if (!existing) {
          // New journalist - add to insert list
          const insertRow: any = {
            name: journalist.name,
            outlet: journalist.outlet,
            email: journalist.email || null,
            phone: journalist.phone || null,
            specialty: journalist.specialty || null,
            bio: journalist.bio || null,
            twitter: journalist.twitter || null,
            website: journalist.website || null,
            verified: journalist.verified,
            credentials_picked_up: journalist.credentialsPickedUp,
            notes: journalist.notes || null,
            press_tier: journalist.pressTier || 'C',
            source: journalist.source || 'bulk_upload'
          }
          if (eventYear) insertRow.event_year = eventYear
          toInsert.push(insertRow)
        } else {
          // Check if any data has changed
          const hasChanges =
            existing.name !== journalist.name ||
            existing.outlet !== journalist.outlet ||
            existing.phone !== (journalist.phone || null) ||
            existing.specialty !== (journalist.specialty || null) ||
            existing.bio !== (journalist.bio || null) ||
            existing.twitter !== (journalist.twitter || null) ||
            existing.website !== (journalist.website || null) ||
            existing.verified !== journalist.verified ||
            existing.credentials_picked_up !== journalist.credentialsPickedUp ||
            existing.notes !== (journalist.notes || null) ||
            (existing as any).press_tier !== (journalist.pressTier || 'C')

          if (hasChanges) {
            // Data has changed - add to update list
            toUpdate.push({
              email: journalist.email!,
              data: {
                name: journalist.name,
                outlet: journalist.outlet,
                phone: journalist.phone || null,
                specialty: journalist.specialty || null,
                bio: journalist.bio || null,
                twitter: journalist.twitter || null,
                website: journalist.website || null,
                verified: journalist.verified,
                credentials_picked_up: journalist.credentialsPickedUp,
                notes: journalist.notes || null,
                press_tier: journalist.pressTier || 'C',
                source: journalist.source || 'bulk_upload'
              }
            })
          } else {
            // No changes - skip
            skipped.push(journalist.email || journalist.name)
          }
        }
      }

      // Perform inserts
      if (toInsert.length > 0) {
        const { data: insertedData, error: insertError } = await supabase
          .from('journalists')
          .insert(toInsert)
          .select()
        
        if (insertError) throw insertError
        added.push(...(insertedData || []).map(convertJournalist))
      }

      // Perform updates
      for (const { email, data } of toUpdate) {
        const { data: updatedData, error: updateError } = await supabase
          .from('journalists')
          .update(data)
          .eq('email', email)
          .select()
          .single()
        
        if (updateError) {
          console.error(`Error updating journalist ${email}:`, updateError)
        } else if (updatedData) {
          updated.push(convertJournalist(updatedData))
        }
      }

      return { added, updated, skipped }
    } catch (error) {
      console.error('Error bulk upserting journalists:', error)
      throw error
    }
  },

  async getByEmail(email: string): Promise<Journalist | null> {
    if (!hasValidCredentials) {
      return null
    }

    try {
      const { data, error } = await supabase
        .from('journalists')
        .select('*')
        .eq('email', email)
        .single()
      
      if (error && error.code !== 'PGRST116') throw error // PGRST116 is "not found"
      return data ? convertJournalist(data) : null
    } catch (error) {
      console.error('Error finding journalist by email:', error)
      return null
    }
  },

  async update(id: string, updates: Partial<Omit<Journalist, 'id'>>): Promise<Journalist | null> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot update journalist')
      return null
    }

    try {
      const dbUpdates: any = {}
      
      // Convert frontend field names to database field names
      if (updates.name !== undefined) dbUpdates.name = updates.name
      if (updates.outlet !== undefined) dbUpdates.outlet = updates.outlet
      if (updates.email !== undefined) dbUpdates.email = updates.email
      if (updates.phone !== undefined) dbUpdates.phone = updates.phone
      if (updates.specialty !== undefined) dbUpdates.specialty = updates.specialty
      if (updates.bio !== undefined) dbUpdates.bio = updates.bio
      if (updates.twitter !== undefined) dbUpdates.twitter = updates.twitter
      if (updates.website !== undefined) dbUpdates.website = updates.website
      if (updates.verified !== undefined) dbUpdates.verified = updates.verified
      if (updates.credentialsPickedUp !== undefined) dbUpdates.credentials_picked_up = updates.credentialsPickedUp
      if (updates.pressTier !== undefined) dbUpdates.press_tier = updates.pressTier
      if (updates.notes !== undefined) dbUpdates.notes = updates.notes
      if (updates.source !== undefined) dbUpdates.source = updates.source

      const { data, error } = await supabase
        .from('journalists')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single()
      
      if (error) throw error
      return data ? convertJournalist(data) : null
    } catch (error) {
      console.error('Error updating journalist:', error)
      throw error
    }
  },

  async delete(id: string): Promise<boolean> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot delete journalist')
      return false
    }

    try {
      console.log('🗑️ Database delete operation for ID:', id);
      
      // First, let's check if the record exists before deleting
      const { data: existingRecord, error: selectError } = await supabase
        .from('journalists')
        .select('id, name, email')
        .eq('id', id)
        .single();
      
      if (selectError) {
        console.error('🗑️ Error finding record to delete:', selectError);
        if (selectError.code === 'PGRST116') {
          console.log('🗑️ Record not found, considering delete successful');
          return true;
        }
        throw selectError;
      }
      
      console.log('🗑️ Found record to delete:', existingRecord);
      
      // Now perform the delete
      const { data, error, count } = await supabase
        .from('journalists')
        .delete()
        .eq('id', id)
        .select();
      
      if (error) {
        console.error('🗑️ Database delete error:', error);
        throw error;
      }
      
      console.log('🗑️ Database delete response data:', data);
      console.log('🗑️ Database delete count:', count);
      
      // Check if the record was actually deleted
      const { data: checkRecord, error: checkError } = await supabase
        .from('journalists')
        .select('id')
        .eq('id', id)
        .single();
      
      if (checkError && checkError.code === 'PGRST116') {
        console.log('✅ Record successfully deleted from database');
        return true;
      } else if (checkRecord) {
        console.error('❌ Record still exists in database after delete!');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting journalist:', error)
      throw error
    }
  },

  async bulkDelete(ids: string[]): Promise<boolean> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot bulk delete journalists')
      return false
    }

    try {
      const { error } = await supabase
        .from('journalists')
        .delete()
        .in('id', ids)
      
      if (error) throw error
      return true
    } catch (error) {
      console.error('Error bulk deleting journalists:', error)
      throw error
    }
  },

  async deleteAll(): Promise<boolean> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot delete all journalists')
      return false
    }

    try {
      const { error } = await supabase
        .from('journalists')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000') // Delete all except impossible ID
      
      if (error) throw error
      return true
    } catch (error) {
      console.error('Error deleting all journalists:', error)
      throw error
    }
  }
}